/**
 * The `brief_review` payload, normalized for rendering.
 *
 * A brief is a different artefact from a plan, not a richer one: a plan says what work will
 * happen, a brief says what change is being made and why, so the maker can steer *above* the
 * level of work items — "wrong approach", "don't touch that agent", "do it silently instead".
 *
 * The pre-slot spellings are still read here because a brief the harness could not normalise
 * (`brief_normalised: false`) passes through raw.
 */
import {
  HarnessOpenQuestion,
  toOpenQuestion,
} from '@flowx/designer/modules/ai-builder/utils/open-question.util'
import {
  asArray,
  asNumber,
  asNumberMap,
  asOptionalNumber,
  asString,
  asStringList,
  asStringMap,
  firstNonEmpty,
  isRecord,
  withRawFallback,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'
import { unslug } from '@flowx/designer/modules/ai-builder/utils/text.util'

/** The three chips on the masthead: what goes away, what is added, who decides. */
export type BriefHeadline = {
  replaces?: string
  adds?: string
  decides?: string
}

/**
 * One before/after pair, anchored to the node or consumer it lands on.
 *
 * `at` is a node id from `path`, or the consumer resource when there is no path. Optional here
 * because the harness defaults it to `''` rather than dropping a row the schema would reject.
 * `summary` leads the row: the after-state when the row is a pair, the whole sentence when not.
 */
export type BriefEdit = {
  at?: string
  before?: string
  after: string
  note?: string
}

/**
 * The route a request takes through the changed nodes, before and after — the diagram as a diff.
 * `before` is the whole point: one lane asks the reader to hold the current process in their head
 * and diff it themselves. `newNodes`/`editedNodes` are the ids badged in the `after` lane.
 */
export type BriefPath = {
  before?: string
  after: string
  newNodes: readonly string[]
  editedNodes: readonly string[]
  caption?: string
}

/** What one consumer is handed, before and after — the shared-object change's answer to a path.
 * `who` is required by the contract and still arrives absent on live briefs, so the pair is kept
 * without it rather than dropped with the one change it describes. */
export type BriefContract = {
  who?: string
  before?: string
  after?: string
}

/**
 * One stage of the mechanism. `who` — which parts a script decides, which a model, which a person
 * — is a field rather than prose because the renderer this replaces derived it from a sentence and
 * styled a human approval as a model decision.
 */
export type BriefStage = {
  name: string
  who: 'script' | 'model' | 'person'
  on: 'always' | 'fallback' | 'failure'
  detail?: string
}

export type BriefDecision = {
  /** The handle structured steering keys on. Derived harness-side from the decision's `topic`, or
   * from its prose when it named none. */
  decisionId: string
  /** What the decision is about: its topic, or the choice itself when it named no topic. */
  decision: string
  /**
   * The fork, when there was one. **Absent is a fact, not a gap:** the harness used to fill a
   * missing question with a copy of `chosen`, so nothing could tell a real fork from a filled-in
   * one. It sends `null` now — so never derive the heading from this alone.
   */
  question?: string
  chosen?: string
  alternatives: readonly string[]
  rationale?: string
  /**
   * `false` when changing this later means redoing committed work. `false | undefined` rather than
   * `boolean`: the wire default is `True` and the mapper drops an explicit `true`, so "reversible"
   * is never a value this carries.
   */
  reversible?: false
}

/** A number that makes the case, and where it came from. `source` is required by the harness
 * because an unsourced statistic is the most persuasive way to be wrong. */
export type BriefStat = {
  label: string
  value: string
  detail?: string
  source?: string
}

/** A legacy row of "Based on": the pre-slot `facts[]`, kept for un-normalised payloads. */
export type BriefFact = {
  text: string
  source?: string
}

export type BriefTable = {
  caption?: string
  columns: readonly string[]
  /** Cells aligned to `columns`. The harness coerces object-shaped rows, so these are arrays. */
  rows: readonly (readonly string[])[]
  footnote?: string
}

/**
 * The brief's reference table, sent as `values`: a scale, statuses, tiers — tied by `usedAt` to the stage that reads it. That tie
 * is what separates a table from an inventory; absent, the section says so rather than pretending
 * the table is read.
 */
export type BriefReferenceTable = BriefTable & { usedAt?: string }

/** A flow to be seen rather than read, as mermaid source. The legacy `sections[].diagram`; a
 * normalised brief carries its diagram as `path` instead. */
export type BriefDiagram = {
  mermaid: string
  caption?: string
}

/** The legacy free-form `sections[]`. Read only from a brief the harness could not normalise; a
 * normalised one has folded these into `stages`, `values`, `rules` and `path`. */
export type BriefSection = {
  title?: string
  body?: string
  source?: string
  steps: readonly string[]
  rules: readonly string[]
  table?: BriefTable
  diagram?: BriefDiagram
}

/**
 * One resource under a scope row, read for somebody who has never opened this application.
 * It may be a change target or merely context: being listed does not imply an edit.
 * `describedElements` of `addressableElements` bounds how specific a plan can be, and
 * zero addressable elements decides whether a work item can land inside the resource at all.
 */
export type BriefResource = {
  name: string
  resourceType?: string
  does?: string
  /** `creates` marks a proposed new resource. Other values (`authored`, `inferred`, `absent`) say
   * where the description came from and mark nothing. */
  source?: string
  addressableElements?: number
  describedElements?: number
  note?: string
}

/** What the change does to one resource type: how many resources it makes and how many existing
 * ones it edits. */
export type BriefScopeChange = { created: number; edited: number }

/**
 * One resource type in the change. `touched` is the harness's count of change targets, never
 * estimated; `change` splits it into new and edited. A listed resource may be context rather than
 * a target, so edits come only from `touched` and are never inferred from the list. A legacy scope
 * line that was only a sentence lands here as a row with a `note` and no counts.
 */
export type BriefScopeLine = {
  resourceType: string
  touched?: number
  change?: BriefScopeChange
  note?: string
  resources: readonly BriefResource[]
}

/** What the change looks like to the person using the application — not what is edited but what
 * somebody will do differently, which is what a maker judges the proposal on. */
export type BriefScreen = {
  where: string
  before?: string
  after?: string
  mustDo?: string
  reuses: readonly string[]
  source?: string
}

/** Something true about the application the maker probably does not know — found by reading,
 * not proposed. Not part of the change, but it changes how the change is judged. */
export type BriefFinding = {
  statement: string
  resourceType?: string
  resourceName?: string
  fixedByThisChange: boolean
}

/** A named counter-check: observed only if the change worked and nothing adjacent broke. The name
 * is what stops a row drifting back into narrating the edit in the past tense. */
export type BriefCheck = {
  name?: string
  check: string
}

/**
 * An independent reader's scores for the brief. It is given the brief, the grounding and the
 * maker's request and nothing else — not the agent's reasoning — which is why a low score is worth
 * showing rather than acting on: a maker may approve a 3, and that stays their call.
 */
export type BriefJudge = {
  judged: boolean
  /** Why it did not run. Present only when `judged` is false. */
  reason?: string
  scores: Readonly<Record<string, number>>
  /** The sentence each dimension was scored on. */
  quotes: Readonly<Record<string, string>>
  /** What each dimension means, in the judge's own words. */
  dimensions: Readonly<Record<string, string>>
  mean?: number
  passes?: boolean
  /** The dimension that scored lowest — what a revision would be aimed at. */
  weakest?: string
}

export type BriefDisplay = {
  revision: number
  title: string
  summary: string
  headline?: BriefHeadline
  why: string
  approach: string
  stats: readonly BriefStat[]
  findings: readonly BriefFinding[]
  path?: BriefPath
  contract?: BriefContract
  edits: readonly BriefEdit[]
  referenceTable?: BriefReferenceTable
  stages: readonly BriefStage[]
  rules: readonly string[]
  screen?: BriefScreen
  scope: readonly BriefScopeLine[]
  risks: readonly string[]
  decisions: readonly BriefDecision[]
  openQuestions: readonly HarnessOpenQuestion[]
  outOfScope: readonly string[]
  acceptance: readonly BriefCheck[]
  /** Legacy: the pre-slot `facts[]`, only ever present on an un-normalised brief. */
  facts: readonly BriefFact[]
  /** Legacy: the pre-slot free-form `sections[]`, only ever present on an un-normalised brief. */
  sections: readonly BriefSection[]
  /** Entries the harness could not read, each named with a reason. NOT a sign the brief is
   * untrustworthy — validation drops the one bad entry and re-validates. */
  droppedEntries: readonly string[]
  /** False when the harness could not coerce the model's output into the contract. */
  normalised: boolean
  normalisationError?: string
  /**
   * Mechanical defects the harness found in its own brief and could not fix. It rewrites a brief
   * twice before showing one with these attached, and narrows its own `is_approvable` on the way
   * past — rendering them turns "not ready" from a closed door into something a maker can act on.
   * The store gates approval on these rather than on the wire flag (`harness-session.store.ts`).
   */
  hardFailures: readonly string[]
  /** Defensible things worth seeing. Shown, never gating. */
  warnings: readonly string[]
  /** What approving the brief also accepts, in the harness's words (`maker_decides`). */
  approvalNotes: readonly string[]
  /** The lines `approvalNotes` say are "under details" (`maker_more`). */
  approvalDetails: readonly string[]
  judge?: BriefJudge
  /** Resources named in the brief that the write plane has no address inside. Not a defect — a
   * whole-resource change is often the honest shape — but it bounds what a work item can say. */
  unaddressableTargets: readonly string[]
  raw?: string
}

export function toBriefDisplay(payload: unknown): BriefDisplay {
  if (!isRecord(payload)) {
    return withRawFallback(emptyBrief(), true, payload)
  }

  // Legacy `changes[]` land as edits: same before/after pair, `area`/`target` as the anchor.
  const edits = asArray(payload['edits']).length ? payload['edits'] : payload['changes']

  const brief: BriefDisplay = {
    // `rev` is assigned by the harness rather than the model, so unlike a plan this has real
    // revision identity and needs no positional heuristic.
    revision: asNumber(payload['rev'], 1),
    title: asString(payload['title']),
    summary: asString(payload['summary']),
    headline: toHeadline(payload['headline']),
    why: asString(payload['why']),
    approach: asString(payload['approach']),
    stats: asArray(payload['stats'])
      .map(toStat)
      .filter((stat) => !!stat.value || !!stat.label),
    findings: asArray(payload['findings'])
      .map(toFinding)
      .filter((finding) => !!finding.statement),
    path: toPath(payload['path']),
    contract: toContract(payload['contract']),
    edits: asArray(edits)
      .map(toEdit)
      .filter((edit) => !!edit.after),
    referenceTable: toReferenceTable(payload['values']),
    stages: asArray(payload['stages'])
      .map(toStage)
      .filter((stage) => !!stage.name),
    rules: asStringList(payload['rules']),
    screen: toScreen(payload['screen']),
    scope: toScope(payload['scope'], payload['orientation']),
    risks: asStringList(payload['risks']),
    decisions: asArray(payload['decisions'])
      .map(toDecision)
      .filter((d) => !!d.decision),
    openQuestions: asArray(payload['open_questions'])
      .map(toOpenQuestion)
      .filter((question) => !!question.question),
    outOfScope: asStringList(payload['out_of_scope']),
    acceptance: asArray(payload['acceptance'])
      .map(toCheck)
      .filter((check) => !!check.check),
    facts: asArray(payload['facts'])
      .map(toFact)
      .filter((fact) => !!fact.text),
    sections: asArray(payload['sections']).map(toSection).filter(hasSectionContent),
    droppedEntries: asStringList(payload['brief_dropped_entries']),
    hardFailures: asStringList(payload['hard_failures']),
    warnings: asStringList(payload['brief_warnings']),
    approvalNotes: asStringList(payload['maker_decides']),
    approvalDetails: asStringList(payload['maker_more']),
    judge: toJudge(payload['judge']),
    unaddressableTargets: asStringList(payload['unaddressable_targets']),
    ...toBriefFlags(payload),
  }

  // Nothing recognisable came through, but the payload was an object — show it rather than an
  // empty one-pager. `toPlanDisplay` has always done this; the object case fell through here.
  const isEmpty =
    !brief.title &&
    !brief.summary &&
    !brief.why &&
    !brief.approach &&
    !brief.edits.length &&
    !brief.decisions.length &&
    !brief.stages.length &&
    !brief.sections.length

  return withRawFallback(brief, isEmpty, payload)
}

function toBriefFlags(
  payload: Record<string, unknown>
): Pick<BriefDisplay, 'normalised' | 'normalisationError'> {
  return {
    // Absent means the frame predates normalisation; only an explicit false is a coercion failure.
    normalised: payload['brief_normalised'] !== false,
    normalisationError: asString(payload['brief_normalisation_error']) || undefined,
  }
}

function toHeadline(value: unknown): BriefHeadline | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const headline: BriefHeadline = {
    replaces: asString(value['replaces']) || undefined,
    adds: asString(value['adds']) || undefined,
    decides: asString(value['decides']) || undefined,
  }

  return headline.replaces || headline.adds || headline.decides ? headline : undefined
}

/** `after` is the one required lane — a path with no after-state draws nothing. `before` is read
 * even when empty: its absence is the state the section has to be honest about. */
function toPath(value: unknown): BriefPath | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const after = asString(value['after'])

  if (!after) {
    return undefined
  }

  return {
    before: asString(value['before']) || undefined,
    after,
    newNodes: asStringList(value['new']),
    editedNodes: asStringList(value['edited']),
    caption: asString(value['caption']) || undefined,
  }
}

function toContract(value: unknown): BriefContract | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const contract: BriefContract = {
    who: asString(value['who']) || undefined,
    before: asString(value['before']) || undefined,
    after: asString(value['after']) || undefined,
  }

  return contract.before || contract.after ? contract : undefined
}

/**
 * One edit, from the slot shape or the legacy `changes[]` row. `after` comes last among the
 * summaries because it is only a summary when the row is a pair — but it is the shape the harness
 * sends, and reading only the older spellings dropped every change from every live brief.
 */
function toEdit(value: unknown): BriefEdit {
  if (!isRecord(value)) {
    return { after: asString(value) }
  }

  return {
    at:
      firstNonEmpty(
        asString(value['at']),
        asString(value['area']),
        asString(value['target']),
        asString(value['locationRef'])
      ) || undefined,
    before: asString(value['before']) || undefined,
    after: firstNonEmpty(
      asString(value['after']),
      asString(value['summary']),
      asString(value['change']),
      asString(value['title'])
    ),
    note:
      firstNonEmpty(
        asString(value['note']),
        asString(value['detail']),
        asString(value['description'])
      ) || undefined,
  }
}

function toReferenceTable(value: unknown): BriefReferenceTable | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const table = toTable(value)

  return table.rows.length || table.caption
    ? { ...table, usedAt: asString(value['used_at']) || undefined }
    : undefined
}

const WHO = new Set(['script', 'model', 'person'])
const ON = new Set(['always', 'fallback', 'failure'])

/** `who` and `on` are enums the harness has already coerced; an unknown value here means an
 * un-normalised payload, and `script`/`always` is what the harness would have filled in. */
function toStage(value: unknown): BriefStage {
  if (!isRecord(value)) {
    return { name: asString(value), who: 'script', on: 'always' }
  }

  const who = asString(value['who'])
  const on = asString(value['on'])

  return {
    name: firstNonEmpty(asString(value['name']), asString(value['label']), asString(value['step'])),
    who: WHO.has(who) ? (who as BriefStage['who']) : 'script',
    on: ON.has(on) ? (on as BriefStage['on']) : 'always',
    detail: firstNonEmpty(asString(value['detail']), asString(value['description'])) || undefined,
  }
}

/**
 * The scope rows with their resources — and, on a legacy payload, the `orientation` rows folded
 * under the row of their resource type, the way the harness now does it itself.
 *
 * A resource type no scope row names becomes a row of its own. Collecting every unnamed one into
 * one row and labelling it with whichever arrived first put a data model under a "process"
 * heading, which is worse than dropping it: the reader has no way to tell the heading is wrong.
 * An un-normalised brief usually carries no `scope[]` at all, so this is the ordinary path for
 * one, not a corner of it.
 */
function toScope(scope: unknown, orientation: unknown): BriefScopeLine[] {
  return foldOrientation(asArray(scope).map(toScopeRow), orientation).map(withChange)
}

/** A scope line as read, before its change is counted. `created` is kept apart from the line
 * because it only means something once the row's resources, folded orientation included, are
 * all known. */
type ScopeRow = Omit<BriefScopeLine, 'change'> & { created?: number }

function foldOrientation(rows: ScopeRow[], orientation: unknown): ScopeRow[] {
  const legacy = asArray(orientation)
    .map(toResource)
    .filter((resource) => !!resource.name)

  if (!legacy.length) {
    return rows
  }

  const named = new Set(rows.map((row) => row.resourceType))
  // Keyed by resource type and in the order they first arrive, so the page reads in the order the
  // model wrote it rather than in whatever order a map happens to hold.
  const leftovers = new Map<string, BriefResource[]>()

  for (const resource of legacy) {
    const resourceType = resource.resourceType ?? ''

    if (!named.has(resourceType)) {
      leftovers.set(resourceType, [...(leftovers.get(resourceType) ?? []), resource])
    }
  }

  return [
    ...rows.map((row) => ({
      ...row,
      resources: [
        ...row.resources,
        ...legacy.filter((resource) => (resource.resourceType ?? '') === row.resourceType),
      ],
    })),
    // A resource the model named no type for heads a row of its own too — "resources" says what
    // little is known about it, and says nothing untrue.
    ...[...leftovers].map(([resourceType, resources]) => ({
      resourceType: resourceType || 'resources',
      resources,
    })),
  ]
}

/** The harness counts per resource type. A legacy line that was only a sentence lands as a row
 * with a note and no counts, so nothing the model wrote about the reach of the change is lost. */
function toScopeRow(value: unknown): ScopeRow {
  if (!isRecord(value)) {
    return { resourceType: asString(value), resources: [] }
  }

  return {
    resourceType: firstNonEmpty(
      asString(value['resource_type']),
      asString(value['label']),
      asString(value['area']),
      asString(value['target'])
    ),
    touched: asCount(value['touched']),
    created: asCount(value['created']),
    note: firstNonEmpty(asString(value['note']), asString(value['detail'])) || undefined,
    resources: asArray(value['resources'])
      .map(toResource)
      .filter((resource) => !!resource.name),
  }
}

function withChange({ created, ...row }: ScopeRow): BriefScopeLine {
  const change = toScopeChange(row.touched, created, row.resources)

  return change ? { ...row, change } : row
}

/** A harness count: a negative number is no count at all, so it reads as absent rather than
 * turning into a negative edit total. */
function asCount(value: unknown): number | undefined {
  const count = asOptionalNumber(value)

  return count !== undefined && count >= 0 ? count : undefined
}

/** `created` says how many of `touched` do not exist yet, so it is capped at `touched` and the rest
 * are edits. Without `touched`, an explicit `created` or the `creates` resources are new and
 * nothing is edited: being listed never makes a resource an edit. */
function toScopeChange(
  touched: number | undefined,
  created: number | undefined,
  resources: readonly BriefResource[]
): BriefScopeChange | undefined {
  if (touched !== undefined) {
    const newCount = Math.min(created ?? 0, touched)

    return { created: newCount, edited: touched - newCount }
  }

  const newCount = created ?? resources.filter((resource) => resource.source === 'creates').length

  return created !== undefined || newCount ? { created: newCount, edited: 0 } : undefined
}

function toResource(value: unknown): BriefResource {
  if (!isRecord(value)) {
    return { name: asString(value) }
  }

  return {
    name: asString(value['name']),
    resourceType:
      firstNonEmpty(asString(value['kind']), asString(value['resource_type'])) || undefined,
    does: firstNonEmpty(asString(value['does']), asString(value['description'])) || undefined,
    source: asString(value['source']) || undefined,
    addressableElements: asOptionalNumber(value['addressable_elements']),
    describedElements: asOptionalNumber(value['described_elements']),
    note: asString(value['note']) || undefined,
  }
}

/** `where` is the only field the contract requires, so a block without one is not a screen block
 * — dropping it beats rendering a heading over nothing. */
function toScreen(value: unknown): BriefScreen | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const where = asString(value['where'])

  if (!where) {
    return undefined
  }

  return {
    where,
    before: asString(value['before']) || undefined,
    after: asString(value['after']) || undefined,
    mustDo: asString(value['must_do']) || undefined,
    reuses: asStringList(value['reuses']),
    source: asString(value['source']) || undefined,
  }
}

function toDecision(value: unknown, index: number): BriefDecision {
  if (!isRecord(value)) {
    return {
      decisionId: `decision-${index + 1}`,
      decision: asString(value),
      alternatives: [],
    }
  }

  const chosen = asString(value['chosen'])
  const alternatives = asArray(value['alternatives'])

  // **`chosen` is last and it is load-bearing.** The harness sends `question: null` for a decision
  // that closed no fork, and a heading read from `question` alone came out empty — which the
  // section's own filter then dropped. `topic` arrives as the slug the id was minted from when the
  // model named none, so it is read back as words, as a plan step's id is.
  return {
    decisionId: asString(value['decision_id']) || `decision-${index + 1}`,
    decision: firstNonEmpty(
      unslug(asString(value['topic'])),
      asString(value['question']),
      asString(value['decision']),
      asString(value['title']),
      chosen
    ),
    question: asString(value['question']) || undefined,
    chosen: chosen || undefined,
    // `||`, not `??`: the harness always sends `alternatives` (`default_factory=list`), so `??`
    // never reached the fallback — and an empty list must not shadow a populated
    // `alternatives_rejected`. The pills that flip a decision are gated on this.
    alternatives: asStringList(alternatives.length ? alternatives : value['alternatives_rejected']),

    rationale: firstNonEmpty(asString(value['rationale']), asString(value['why'])) || undefined,
    // Only an explicit false marks a decision as hard to undo; absent means unknown.
    reversible: value['reversible'] === false ? false : undefined,
  }
}

/** A verdict the judge could not form reports `judged: false` with a reason — a different fact
 * from a bad brief, and it must not render as one. */
function toJudge(value: unknown): BriefJudge | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  return {
    judged: value['judged'] === true,
    reason: asString(value['reason']) || undefined,
    scores: asNumberMap(value['scores']),
    quotes: asStringMap(value['quotes']),
    dimensions: asStringMap(value['dimensions']),
    mean: typeof value['mean'] === 'number' ? value['mean'] : undefined,
    passes: typeof value['passes'] === 'boolean' ? value['passes'] : undefined,
    weakest: asString(value['weakest']) || undefined,
  }
}

function toStat(value: unknown): BriefStat {
  if (!isRecord(value)) {
    return { label: '', value: asString(value) }
  }

  // The model has been seen naming this `name` rather than `label`. The harness aliases it now,
  // but reading both keeps an un-normalised payload legible.
  return {
    label: firstNonEmpty(asString(value['label']), asString(value['name'])),
    value: asString(value['value']),
    detail: asString(value['detail']) || undefined,
    source: asString(value['source']) || undefined,
  }
}

function toFact(value: unknown): BriefFact {
  if (!isRecord(value)) {
    return { text: asString(value) }
  }

  return {
    text: firstNonEmpty(asString(value['text']), asString(value['fact']), asString(value['value'])),
    source: asString(value['source']) || undefined,
  }
}

function toTable(table: Record<string, unknown>): BriefTable {
  return {
    caption: asString(table['caption']) || undefined,
    columns: asStringList(table['columns']),
    // Rows are arrays of cells aligned to `columns`; anything else is coerced to one cell rather
    // than dropped, so a malformed row still shows its content.
    rows: asArray(table['rows']).map((row) =>
      Array.isArray(row)
        ? row.map((cell) => asString(cell))
        : isRecord(row)
          ? Object.values(row).map((cell) => asString(cell))
          : [asString(row)]
    ),
    footnote: asString(table['footnote']) || undefined,
  }
}

function toSection(value: unknown): BriefSection {
  if (!isRecord(value)) {
    return { body: asString(value), steps: [], rules: [] }
  }

  const table = isRecord(value['table']) ? value['table'] : null
  const diagram = isRecord(value['diagram']) ? value['diagram'] : null
  const mermaid = diagram ? asString(diagram['mermaid']) : ''

  return {
    title: asString(value['title']) || undefined,
    body: asString(value['body']) || undefined,
    source: asString(value['source']) || undefined,
    steps: asArray(value['steps']).map(toStepText).filter(Boolean),
    rules: asStringList(value['rules']),
    ...(table && { table: toTable(table) }),
    // `diagram &&` is what narrows it for the compiler: `mermaid` cannot be set without it.
    ...(diagram &&
      mermaid && {
        diagram: { mermaid, caption: asString(diagram['caption']) || undefined },
      }),
  }
}

/** A section with a title but nothing under it is a heading with no content — drop it. */
function hasSectionContent(section: BriefSection): boolean {
  return !!(
    section.body ||
    section.steps.length ||
    section.rules.length ||
    section.table ||
    section.diagram
  )
}

/** One numbered step of a legacy section. The harness sent `{name, label?, detail?, source?}`, so
 * the line reads "Derive — compute the score…"; the spec's plain spellings are still read. */
function toStepText(value: unknown): string {
  if (!isRecord(value)) {
    return asString(value)
  }

  const name = firstNonEmpty(
    asString(value['name']),
    asString(value['label']),
    asString(value['text']),
    asString(value['step']),
    asString(value['title'])
  )
  const detail = firstNonEmpty(asString(value['detail']), asString(value['description']))

  return [name, detail].filter(Boolean).join(' — ')
}

function toFinding(value: unknown): BriefFinding {
  if (!isRecord(value)) {
    return { statement: asString(value), fixedByThisChange: false }
  }

  return {
    statement: firstNonEmpty(
      asString(value['statement']),
      asString(value['text']),
      asString(value['finding'])
    ),
    resourceType: asString(value['resource_type']) || undefined,
    resourceName:
      firstNonEmpty(asString(value['resource_name']), asString(value['resource_id'])) || undefined,
    fixedByThisChange: value['fixed_by_this_change'] === true,
  }
}

/** A check, from the slot shape or a legacy bare sentence. */
function toCheck(value: unknown): BriefCheck {
  if (!isRecord(value)) {
    return { check: asString(value) }
  }

  return {
    name: asString(value['name']) || undefined,
    check: firstNonEmpty(asString(value['check']), asString(value['text'])),
  }
}

function emptyBrief(): BriefDisplay {
  return {
    revision: 1,
    title: '',
    summary: '',
    why: '',
    approach: '',
    stats: [],
    findings: [],
    edits: [],
    stages: [],
    rules: [],
    scope: [],
    risks: [],
    decisions: [],
    openQuestions: [],
    outOfScope: [],
    acceptance: [],
    facts: [],
    sections: [],
    droppedEntries: [],
    normalised: true,
    hardFailures: [],
    warnings: [],
    approvalNotes: [],
    approvalDetails: [],
    unaddressableTargets: [],
  }
}
